import fetch from 'isomorphic-fetch'
import { authHeader, handleResponse } from './helpers'
import { API_URL } from '../../constants/Config'

export const accountService = {
  getInfo,
  getApiKey,
  reset,
  getUsers,
  inviteUser,
  removeUser,
  updateRole,
  updateUserMcp,
  updateMyMcpPermissions,
  getPricing,
  checkout,
  getBillingPortalUrl,
  getUsage,
  getMembers,
  // MCP Server billing
  getMcpBilling,
  mcpCheckout,
  mcpAnnualInvoice,
  setMcpSeats,
  previewMcpSeats,
  // MCP-35: the ticket data store
  getTicketStore,
  setTicketStore,
  startTicketStore,
}

async function getInfo(auth_token, product) {
  const url = `${API_URL}/account?product=${product}`;
  const options = {
    method: 'GET',
    headers: authHeader(auth_token)
  };
  return fetch(url, options)
    .then((response) => handleResponse(response))
}

async function getApiKey(auth_token) {
  const url = `${API_URL}/account/api_key`
  const options = {
    method: 'GET',
    headers: authHeader(auth_token),
  }
  return fetch(url, options)
    .then(response => handleResponse(response))
    .then(response => response.api_key)
}

async function reset(auth_token, product) {
  let url = `${API_URL}/account/reset`
  if (product) {
    url += `?product=${product}`
  }
  const options = {
    method: 'POST',
    headers: authHeader(auth_token, {
      'Content-Type': 'application/json',
    }),
    body: JSON.stringify({}),
  }
  return fetch(url, options).then(response => handleResponse(response))
}

async function getUsers(auth_token) {
  const url = `${API_URL}/account/users`
  const options = {
    method: 'GET',
    headers: authHeader(auth_token, {
      'Content-Type': 'application/json',
    }),
  }
  return fetch(url, options).then((response) => handleResponse(response))
}

// mcp_connection ('own' | 'shared') is only meaningful for the MCP server product and
// is left out of the request body for every other product.
// give_seat (MCP server, paid accounts only): false invites without allocating a seat.
// owner_permissions (MCP server): what the owner allows these people from the start, e.g.
// {mcp_allow_write: false} for read only. Left out, they get whatever the account allows.
async function inviteUser(auth_token, email, role, product, mcp_connection, give_seat, owner_permissions) {
  const url = `${API_URL}/account/users?product=${product}`
  const body = {
    email: email,
    role: role
  }
  if (product === 'mcpserver' && give_seat === false) {
    body.give_seat = false
  }
  if (product === 'mcpserver' && mcp_connection) {
    body.mcp_connection = mcp_connection
  }
  if (product === 'mcpserver' && owner_permissions) {
    body.mcp_permissions_owner = owner_permissions
  }
  const options = {
    method: 'POST',
    headers: authHeader(auth_token, {
      'Content-Type': 'application/json',
    }),
    body: JSON.stringify(body),
  }
  return fetch(url, options).then((response) => handleResponse(response))
}

async function removeUser(auth_token, userId) {
  const url = `${API_URL}/account/users/${userId}`
  const options = {
    method: 'DELETE',
    headers: authHeader(auth_token),
  }
  return fetch(url, options).then((response) => handleResponse(response))
}

async function updateRole(auth_token, userId,role) {
  const url = `${API_URL}/account/users/${userId}`
  const options = {
    method: 'PUT',
    headers: authHeader(auth_token, {
      'Content-Type': 'application/json',
    }),
    body: JSON.stringify({
      role: role
    }),
  }
  return fetch(url, options).then((response) => handleResponse(response))
}

// E13: fields is {mcp_enabled?: boolean, mcp_connection?: 'own'|'shared',
// mcp_permissions_owner?: {setting: boolean}}. The API only accepts these for the MCP server
// product, hence the product query parameter -- which is why what the owner allows a person
// goes through here and never through updateRole, which sends none.
async function updateUserMcp(auth_token, userId, fields) {
  const url = `${API_URL}/account/users/${userId}?product=mcpserver`
  const options = {
    method: 'PUT',
    headers: authHeader(auth_token, {
      'Content-Type': 'application/json',
    }),
    body: JSON.stringify(fields),
  }
  return fetch(url, options).then((response) => handleResponse(response))
}

// A user narrowing their own write permissions. A different route to updateUserMcp above:
// that one is owner-only and targets a user by id, this one always targets the caller and is
// open to everyone, because narrowing your own permissions is nobody else's business.
// Returns the refreshed mcp_user, so the caller need not reload to see the result.
async function updateMyMcpPermissions(auth_token, permissions) {
  const url = `${API_URL}/account/mcp_permissions?product=mcpserver`
  const options = {
    method: 'PUT',
    headers: authHeader(auth_token, {
      'Content-Type': 'application/json',
    }),
    body: JSON.stringify(permissions),
  }
  return fetch(url, options).then((response) => handleResponse(response))
}

async function getPricing(auth_token, product) {
  const url = `${API_URL}/account/pricing?product=${product}`
  const options = {
    method: 'GET',
    headers: authHeader(auth_token, {
      'Content-Type': 'application/json',
    }),
  }
  return fetch(url, options).then((response) => handleResponse(response))
}
async function checkout(auth_token, product, pricingPlans) {
  const url = `${API_URL}/account/checkout`
  const options = {
    method: 'POST',
    headers: authHeader(auth_token, {
      'Content-Type': 'application/json',
    }),
    body: JSON.stringify({product, plans: pricingPlans}),
  }
  return fetch(url, options).then((response) => handleResponse(response))
}

// product is optional; the MCP server gets its own portal configuration (invoices, payment
// method, cancel at period end - seat changes go through the app).
async function getBillingPortalUrl(auth_token, product) {
  const url = `${API_URL}/account/billing_portal${product ? '?product=' + product : ''}`
  const options = {
    method: 'GET',
    headers: authHeader(auth_token, {
      'Content-Type': 'application/json',
    }),
  }
  return fetch(url, options).then((response) => handleResponse(response))
}

async function getUsage(auth_token, query) {
  const url = `${API_URL}/account/app_usage?${new URLSearchParams(query)}`
  const options = {
    method: 'GET',
    headers: authHeader(auth_token),
  }
  return fetch(url, options).then(response => handleResponse(response))
}

// --- MCP Server billing (owner-only writes; the API enforces the role) -------------------

async function getMcpBilling(auth_token) {
  const url = `${API_URL}/account/mcp/billing`
  return fetch(url, {method: 'GET', headers: authHeader(auth_token)}).then(handleResponse)
}

// seats: how many to buy (the owner allocates them on Users afterwards); period 'month' or
// 'year', both paid by card. Returns {url} for Stripe Checkout.
async function mcpCheckout(auth_token, seats, period) {
  const url = `${API_URL}/account/mcp/checkout`
  const options = {
    method: 'POST',
    headers: authHeader(auth_token, {'Content-Type': 'application/json'}),
    body: JSON.stringify({seats, period}),
  }
  return fetch(url, options).then(handleResponse)
}

// Annual, paid by invoice: details = {company, line1, line2, city, postal_code, country, state, vat_id, po_number}
async function mcpAnnualInvoice(auth_token, seats, details) {
  const url = `${API_URL}/account/mcp/annual_invoice`
  const options = {
    method: 'POST',
    headers: authHeader(auth_token, {'Content-Type': 'application/json'}),
    body: JSON.stringify({seats, ...details}),
  }
  return fetch(url, options).then(handleResponse)
}

// What changing to `seats` would cost: Stripe's prorated figure for an increase, the next
// charge, and the card on file. Read-only.
async function previewMcpSeats(auth_token, seats) {
  const url = `${API_URL}/account/mcp/seats/preview`
  const options = {
    method: 'POST',
    headers: authHeader(auth_token, {'Content-Type': 'application/json'}),
    body: JSON.stringify({seats}),
  }
  return fetch(url, options).then(handleResponse)
}

async function setMcpSeats(auth_token, seats) {
  const url = `${API_URL}/account/mcp/seats`
  const options = {
    method: 'POST',
    headers: authHeader(auth_token, {'Content-Type': 'application/json'}),
    body: JSON.stringify({seats}),
  }
  return fetch(url, options).then(handleResponse)
}

// --- MCP-35: the ticket data store (owner only; the API enforces the role) ---------------------

async function getTicketStore(auth_token) {
  const url = `${API_URL}/account/mcp/ticket_store`
  return fetch(url, {method: 'GET', headers: authHeader(auth_token)}).then(handleResponse)
}

// fields: {enabled?: boolean, window_days?: number}. Returns the store's new status.
async function setTicketStore(auth_token, fields) {
  const url = `${API_URL}/account/mcp/ticket_store`
  const options = {
    method: 'PUT',
    headers: authHeader(auth_token, {'Content-Type': 'application/json'}),
    body: JSON.stringify(fields),
  }
  return fetch(url, options).then(handleResponse)
}

// The first sync. Returns the store's new status.
async function startTicketStore(auth_token) {
  const url = `${API_URL}/account/mcp/ticket_store/start`
  return fetch(url, {method: 'POST', headers: authHeader(auth_token)}).then(handleResponse)
}

async function getMembers(auth_token, query) {
  const url = `${API_URL}/account/app_usage?${new URLSearchParams(query)}`
  const options = {
    method: 'GET',
    headers: authHeader(auth_token),
  }
  return fetch(url, options).then(response => handleResponse(response))
}
